import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Info } from 'lucide-react'
import roleService from '@services/roleService'
import AssociatedRoles from '@components/features/projects/AssociatedRoles'

/**
 * RolesForm Component
 *
 * Wrapper component for role selection in Edit Project.
 * Uses the shared AssociatedRoles component for consistent UI/UX.
 *
 * @param {Array} value - Currently selected role IDs
 * @param {Function} onChange - Callback when selection changes (receives array of role IDs)
 */
const RolesForm = ({ value = [], onChange }) => {
  const [availableRoles, setAvailableRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchAvailableRoles()
  }, [])

  const fetchAvailableRoles = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch company-assignable roles
      const response = await roleService.getCompanyAssignableRoles()

      if (response.success || response.data) {
        const roles = response.data || response || []
        setAvailableRoles(roles)
      } else {
        setError('Failed to load roles')
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err)
      setError('Failed to load available roles. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (error) {
    return (
      <div className="roles-form">
        <div className="roles-form__error">
          <p>{error}</p>
          <button className="btn btn--secondary" onClick={fetchAvailableRoles}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="roles-form">
      <div className="roles-form__info-banner">
        <Info size={16} />
        <span>
          Select the roles that will be involved in this project. Team members will be assigned to these roles later.
        </span>
      </div>

      <AssociatedRoles
        roles={availableRoles}
        selectedRoles={value}
        onChange={onChange}
        loading={loading}
      />
    </div>
  )
}

RolesForm.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
}

RolesForm.defaultProps = {
  value: [],
}

export default RolesForm
